/** File-picker accept strings (aligned with product copy). */
export const VIDEO_UPLOAD_ACCEPT =
  ".mp4,.mov,.webm,.avi,.mkv,.m4v,.mpeg,.mpg,video/*";

export const AUDIO_UPLOAD_ACCEPT =
  ".mp3,.wav,.aac,.m4a,.ogg,.amr,audio/*";

export const IMAGE_UPLOAD_ACCEPT =
  ".jpg,.jpeg,.png,.webp,.gif,.bmp,.tiff,.avif,image/*";

const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "avi",
  "wmv",
  "webm",
  "mkv",
  "mpg",
  "mpeg",
  "m4v",
]);

const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "aac", "ogg", "m4a", "amr"]);

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp"]);

const VIDEO_MIMES = new Set([
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
  "video/webm",
  "video/x-matroska",
  "video/mpeg",
]);

const AUDIO_MIMES = new Set([
  "application/ogg",
  "audio/mpeg",
  "audio/mp3",
  "audio/x-mpeg",
  "audio/wav",
  "audio/wave",
  "audio/x-wav",
  "audio/aac",
  "audio/x-aac",
  "audio/ogg",
  "audio/vorbis",
  "audio/m4a",
  "audio/mp4",
  "audio/mp4a-latm",
  "audio/x-m4a",
  "audio/x-mp4a-latm",
  "audio/amr",
  "audio/amr-wb",
  "audio/3gpp",
]);

/** Explicitly blocked even when picker uses audio/* */
const BLOCKED_AUDIO_EXTENSIONS = new Set(["flac"]);
const BLOCKED_AUDIO_MIMES = new Set(["audio/flac", "audio/x-flac"]);

const GENERIC_MIMES = new Set(["application/octet-stream"]);

const IMAGE_MIMES = new Set(["image/png", "image/jpeg", "image/webp"]);
const AUDIO_EXTENSION_VIDEO_MIMES = new Set(["video/mp4"]);

function fileExtension(name = "") {
  const base = String(name).split(/[/\\]/).pop() || "";
  const dot = base.lastIndexOf(".");
  return dot >= 0 ? base.slice(dot + 1).toLowerCase() : "";
}

function matchesByExtension(ext, allowed) {
  return Boolean(ext && allowed.has(ext));
}

function normalizeMime(type = "") {
  return String(type || "").split(";")[0].trim().toLowerCase();
}

function hasUsefulMime(type) {
  const mime = normalizeMime(type);
  return Boolean(mime && !GENERIC_MIMES.has(mime));
}

function matchesByMime(type, allowed, prefix) {
  const mime = normalizeMime(type);
  if (!mime) return false;
  if (allowed.has(mime)) return true;
  if (prefix && mime.startsWith(prefix)) return true;
  return false;
}

function isBlockedAudioFile(file) {
  const ext = fileExtension(file?.name);
  if (BLOCKED_AUDIO_EXTENSIONS.has(ext)) return true;
  const mime = normalizeMime(file?.type);
  return BLOCKED_AUDIO_MIMES.has(mime);
}

function hasAllowedAudioExtension(file) {
  return matchesByExtension(fileExtension(file?.name), AUDIO_EXTENSIONS);
}

function isAudioNamedMp4(file) {
  return (
    hasAllowedAudioExtension(file) &&
    AUDIO_EXTENSION_VIDEO_MIMES.has(normalizeMime(file?.type))
  );
}

/**
 * @param {{ name?: string; type?: string } | null | undefined} file
 */
export function isAllowedVideoFile(file) {
  if (!file) return false;
  const ext = fileExtension(file.name);
  if (matchesByExtension(ext, VIDEO_EXTENSIONS)) return true;
  if (matchesByMime(file.type, VIDEO_MIMES, "video/")) {
    return ext ? matchesByExtension(ext, VIDEO_EXTENSIONS) : true;
  }
  return false;
}

/**
 * Detects explicit video uploads by extension or MIME.
 * @param {{ name?: string; type?: string } | null | undefined} file
 */
export function isLikelyVideoFile(file) {
  if (!file) return false;
  const ext = fileExtension(file.name);
  return (
    matchesByExtension(ext, VIDEO_EXTENSIONS) ||
    matchesByMime(file.type, VIDEO_MIMES, "video/")
  );
}

/**
 * @param {{ name?: string; type?: string } | null | undefined} file
 */
export function isAllowedAudioFile(file) {
  if (!file) return false;
  if (isBlockedAudioFile(file)) return false;
  if (!hasAllowedAudioExtension(file)) return false;
  if (isLikelyVideoFile(file) && !isAudioNamedMp4(file)) return false;
  if (!hasUsefulMime(file.type)) return true;
  return (
    matchesByMime(file.type, AUDIO_MIMES, "audio/") || isAudioNamedMp4(file)
  );
}

/**
 * Audio upload validation. Renamed MP4 files with an audio extension are allowed
 * because the upload API can extract their audio track.
 * @param {{ name?: string; type?: string } | null | undefined} file
 * @returns {Promise<{ valid: boolean; likelyVideo: boolean }>}
 */
export async function validateAudioUploadFile(file) {
  if (!file) return { valid: false, likelyVideo: false };
  const likelyVideo = isLikelyVideoFile(file) && !isAudioNamedMp4(file);
  return {
    valid: !likelyVideo && isAllowedAudioFile(file),
    likelyVideo,
  };
}

/**
 * Supported images: PNG, JPG, JPEG, WebP (picker accept is wider).
 * @param {{ name?: string; type?: string } | null | undefined} file
 */
export function isAllowedImageFile(file) {
  if (!file) return false;
  const ext = fileExtension(file.name);
  if (matchesByExtension(ext, IMAGE_EXTENSIONS)) return true;
  const mime = normalizeMime(file.type);
  if (IMAGE_MIMES.has(mime)) return true;
  if (mime.startsWith("image/")) {
    return ext ? matchesByExtension(ext, IMAGE_EXTENSIONS) : false;
  }
  return false;
}
